import React, { useMemo } from 'react';

import { OrgChartHr } from 'routes/HumanResourcesModule/PayState/helpers/types';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { GridEchelons } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/helpers/types';
import AgentArrivalForm, {
    FormState,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/AgentArrivalHypothesisModal/AgentArrivalForm';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { AgentArrivalHypothesisNonIndiciaireStep } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/components/AgentArrivalHypothesisNonIndiciaireStep';
import { AgentArrivalHypothesisIndiciaireStep } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/components/AgentArrivalHypothesisIndiciaireStep';
import {
    MantyStatus,
    RecurrenceEnum,
    RetirementSystem,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { AgentArrivalHypothesisThirdStep } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/components/AgentArrivalHypothesisThirdStep';
import { useGetHrSimulationPayConstants } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/helpers/hooks/payConstants';
import { SPECIAL_RATE } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/constants';

type Props = {
    show: boolean;
    onClose: () => void;
    orgChartHrs: OrgChartHr[];
    disableOrgChartSelector?: boolean;
    onDelete?: () => void;
    hrSimulation: HrSimulation;
    onClickSave: () => void;
    editMode: boolean;
    officialGrades: GridEchelons[];
    formState: FormState;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>;
};
const DEFAULT_INDEX_POINT_VALUE = 4.92;

const RecurrentNonIndiciaireStatuses = [
    MantyStatus.INTERN,
    MantyStatus.APPRENTICE,
];
const NonRecurrentIndiciaireStatuses = [MantyStatus.SUBSTITUTE_CONTRACTOR];
const AgentArrivalHypothesisBaseModal = ({
    officialGrades,
    show,
    onClose,
    orgChartHrs,
    formState,
    setFormState,
    hrSimulation,
    disableOrgChartSelector = false,
    onDelete,
    onClickSave,
    editMode,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const isRecurrentIndiciaire =
        formState.recurrence === RecurrenceEnum.RECURRENT &&
        !RecurrentNonIndiciaireStatuses.includes(formState.mantyStatus);
    const isNonRecurrentIndiciaire =
        formState.recurrence === RecurrenceEnum.NON_RECURRENT &&
        NonRecurrentIndiciaireStatuses.includes(formState.mantyStatus);
    const isIndiciaire = isRecurrentIndiciaire || isNonRecurrentIndiciaire;
    const { data: hrSimulationPayConstantResponse } =
        useGetHrSimulationPayConstants(hrSimulation.id);
    const { rateCnraclHypothesis } = hrSimulationPayConstantResponse ?? {};
    const isSaveEnabled = Boolean(
        formState.selectedOrgChartHrSoftwareId &&
            formState.selectedStartDate &&
            !Number.isNaN(new Date(formState.selectedStartDate).getTime()) &&
            formState.agentETP &&
            formState.mantyStatus,
    );
    const isIndiciaireStepValid = Boolean(
        isIndiciaire &&
            formState.retirementSystem &&
            formState.officialGrade &&
            Number(formState.echelon) > 0,
    );
    const isNonIndiciaireStepValid = Boolean(
        !isIndiciaire &&
            formState.retirementSystem &&
            formState.majoredSalary > 0,
    );
    const firstStep = useMemo(
        () => (
            <AgentArrivalForm
                formState={formState}
                setFormState={setFormState}
                hrSimulation={hrSimulation}
                orgChartHrs={orgChartHrs}
                disableOrgChartSelector={disableOrgChartSelector}
            />
        ),
        [formState],
    );
    const secondStep = (
        <>
            {isIndiciaire ? (
                <AgentArrivalHypothesisIndiciaireStep
                    formState={formState}
                    officialGrades={officialGrades}
                    setFormState={setFormState}
                />
            ) : (
                <AgentArrivalHypothesisNonIndiciaireStep
                    formState={formState}
                    setFormState={setFormState}
                />
            )}
        </>
    );

    const thirdStep: ModalStepT = {
        stepBackAction: {
            label: t('common.previous'),
        },
        confirmAction: {
            label: t('common.validate'),
            onClick: onClickSave,
        },
        title: t('humanResources.simulation.addAgent.salaryCalculation'),
        node: (
            <AgentArrivalHypothesisThirdStep
                formState={formState}
                setFormState={setFormState}
                rateCnraclDefaultValue={
                    rateCnraclHypothesis?.defaultValue
                        ? rateCnraclHypothesis?.defaultValue / 100
                        : SPECIAL_RATE
                }
            />
        ),
    };
    const steps: ModalStepT[] = [
        {
            stepBackAction: {
                label: t('common.cancel'),
            },
            title: t('humanResources.simulation.addAgent.entry'),
            node: firstStep,
            leftActionGroup: (
                <>
                    {editMode && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={onDelete}
                        >
                            {t('common.delete')}
                        </Button>
                    )}
                </>
            ),
            confirmAction: {
                onClick: () => {
                    if (!isIndiciaire)
                        setFormState({
                            ...formState,
                            retirementSystem: RetirementSystem.IRCANTEC,
                        });
                },
                enabled: isSaveEnabled,
            },
        },
        {
            confirmAction: {
                label: t(isIndiciaire ? 'common.next' : 'common.validate'),
                enabled: isIndiciaire
                    ? isIndiciaireStepValid
                    : isNonIndiciaireStepValid,
                onClick: () => {
                    if (isIndiciaire) {
                        const officialGradesData = officialGrades.find(
                            (item) =>
                                item.grade === formState.officialGrade &&
                                item.filiere === formState.filiere &&
                                item.echelon === formState.echelon,
                        );
                        const majoredIndex =
                            Number(officialGradesData?.majoredIndex) || 0;
                        setFormState({
                            ...formState,
                            // To determine the majored salary, the ETP is multiplied by the base salary
                            // for some grades the majored index value is '-'. in this case, the base salary is the raw salary
                            majoredSalary:
                                (officialGradesData?.majoredIndex !== '-'
                                    ? majoredIndex * DEFAULT_INDEX_POINT_VALUE
                                    : officialGradesData.rawSalary) *
                                formState.agentETP,
                        });
                    } else {
                        onClickSave();
                    }
                },
            },
            stepBackAction: {
                label: t('common.previous'),
            },
            leftActionGroup: (
                <>
                    {editMode && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={onDelete}
                        >
                            {t('common.delete')}
                        </Button>
                    )}
                </>
            ),
            title: t(
                `humanResources.simulation.addAgent.${isIndiciaire ? 'indiciaire' : 'nonIndiciaire'}SecondStepModalTitle`,
            ),
            node: secondStep,
        },
    ];
    if (isIndiciaire) {
        steps.push(thirdStep);
    }

    return (
        <MultiStepModal
            isOpen={show}
            onClose={onClose}
            maxWidth="sm"
            isSmallHeight
            steps={steps}
        />
    );
};

export default AgentArrivalHypothesisBaseModal;
